import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, ClipboardList, Send, Loader2, Laptop, MonitorSmartphone, HardDrive, Package,
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_META = {
  laptop:  { label: 'Laptop', icon: Laptop },
  phone:   { label: 'Phone', icon: MonitorSmartphone },
  tablet:  { label: 'Tablet', icon: MonitorSmartphone },
  monitor: { label: 'Monitor', icon: HardDrive },
  headset: { label: 'Headset', icon: Package },
  other:   { label: 'Other', icon: Package },
};

const STATUS_COLOR = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-blue-100 text-blue-700',
  denied: 'bg-red-100 text-red-700',
  fulfilled: 'bg-emerald-100 text-emerald-700',
};

const EquipmentRequestPage = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [form, setForm] = useState({ category: 'laptop', model_preference: '', reason: '', urgency: 'normal' });
  const [submitting, setSubmitting] = useState(false);
  const [requests, setRequests] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, d] = await Promise.all([
        api.get('/hr/device-requests/mine'),
        api.get('/hr/devices/mine'),
      ]);
      setRequests(r.data.requests || []);
      setDevices(d.data.devices || []);
    } catch (err) {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.reason.trim() || form.reason.trim().length < 10) {
      toast.error('Please describe why you need this (at least 10 characters).');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/hr/device-requests', form);
      toast.success('Request submitted — HR will review');
      setForm({ category: 'laptop', model_preference: '', reason: '', urgency: 'normal' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/employee/center')} data-testid="eq-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <Link to="/employee/center" className="inline-block hover:opacity-70 transition-opacity" data-testid="eq-title-link">
              <h1 className="text-2xl font-extrabold text-slate-900 flex items-center gap-2">
                <ClipboardList className="w-6 h-6 text-indigo-600" /> Request Equipment
              </h1>
            </Link>
            <p className="text-sm text-slate-500">Ask HR for a laptop, phone or other work equipment.</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 md:p-6 grid md:grid-cols-2 gap-6">
        {/* Request form */}
        <Card>
          <CardContent className="p-5 space-y-3">
            <h2 className="font-bold text-slate-800">New request</h2>
            <div>
              <label className="text-xs uppercase font-semibold text-slate-500">Category</label>
              <select className="w-full border rounded-md px-3 py-2 mt-1" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} data-testid="eq-form-category">
                {Object.entries(CATEGORY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase font-semibold text-slate-500">Model preference (optional)</label>
              <Input placeholder='e.g. "MacBook Pro 14" M3"' value={form.model_preference} onChange={(e) => setForm({...form, model_preference: e.target.value})} data-testid="eq-form-model" />
            </div>
            <div>
              <label className="text-xs uppercase font-semibold text-slate-500">Reason</label>
              <Textarea placeholder="Why do you need this? (min 10 chars)" value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})} data-testid="eq-form-reason" rows={4} />
            </div>
            <div>
              <label className="text-xs uppercase font-semibold text-slate-500">Urgency</label>
              <select className="w-full border rounded-md px-3 py-2 mt-1" value={form.urgency} onChange={(e) => setForm({...form, urgency: e.target.value})}>
                <option value="low">Low</option><option value="normal">Normal</option><option value="high">High — blocking my work</option>
              </select>
            </div>
            <Button onClick={submit} disabled={submitting} className="w-full" data-testid="eq-form-submit">
              {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              Submit request
            </Button>
          </CardContent>
        </Card>

        {/* My devices + my requests */}
        <div className="space-y-4">
          <Card>
            <CardContent className="p-5">
              <h2 className="font-bold text-slate-800 mb-3">Equipment assigned to me</h2>
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              ) : devices.length === 0 ? (
                <p className="text-sm text-slate-500">Nothing yet — submit a request on the left.</p>
              ) : (
                <div className="space-y-2" data-testid="eq-my-devices">
                  {devices.map((d) => {
                    const Icon = CATEGORY_META[d.category]?.icon || Package;
                    return (
                      <div key={d.id} className="flex items-center gap-3 p-2 rounded-md bg-slate-50" data-testid={`eq-my-device-${d.id}`}>
                        <div className="w-8 h-8 rounded-md bg-white flex items-center justify-center"><Icon className="w-4 h-4 text-slate-500" /></div>
                        <div className="flex-1">
                          <p className="text-sm font-medium">{d.model}</p>
                          <p className="text-[10px] text-slate-400 uppercase">{d.asset_tag} · {d.condition}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <h2 className="font-bold text-slate-800 mb-3">My requests</h2>
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              ) : requests.length === 0 ? (
                <p className="text-sm text-slate-500">No requests yet.</p>
              ) : (
                <div className="space-y-2" data-testid="eq-my-requests">
                  {requests.map((r) => (
                    <div key={r.id} className="p-3 rounded-md bg-slate-50 space-y-1" data-testid={`eq-my-request-${r.id}`}>
                      <div className="flex justify-between items-start gap-2">
                        <p className="text-sm font-medium">
                          {CATEGORY_META[r.category]?.label || r.category}
                          {r.model_preference && <span className="text-xs text-slate-500"> · {r.model_preference}</span>}
                        </p>
                        <Badge className={STATUS_COLOR[r.status] || ''}>{r.status}</Badge>
                      </div>
                      <p className="text-xs text-slate-600 line-clamp-2">{r.reason}</p>
                      {r.decision_notes && <p className="text-xs italic text-slate-500">HR: {r.decision_notes}</p>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default EquipmentRequestPage;
